import PropTypes from "prop-types";
import React from "react";
import styled, { css } from "styled-components";
import Check from "components/icons/check";
import FieldError from "components/FieldError";
import formatFieldErrors from "lib/format-field-errors";

const CheckCon = styled.div`
  background-color: ${({ theme }) => theme.themeColors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.themeColors.secondary};
  display: inline-block;
  height: 15px;
  line-height: 17px;
  margin-right: 5px;
  width: 15px;

  ${({ checked, theme }) =>
    checked &&
    css`
      background-color: ${theme.themeColors.primary};
    `};

  ${({ hasError, theme }) =>
    hasError &&
    css`
      border-color: ${theme.themeColors.orange2};
    `};

  svg {
    height: 15px;
    width: 15px;
    path {
      stroke-width: 3;
    }
  }
`;

const Container = styled.div`
  cursor: pointer;
  display: inline-block;
  line-height: 1.0625rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    display: none;
  }

  input[type="checkbox"]:checked + ${CheckCon} {
    opacity: 1;
    background-color: ${({ theme }) => theme.themeColors.primary};
    border: none;
    svg {
      opacity: 1;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;

const Label = styled.label`
  color: ${({ theme }) => theme.themeColors.goodfriendDark};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  position: relative;

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 1rem;
        font-weight: 400;
        line-height: 0.3125rem;
        position: absolute;
        right: 4px;
        top: 50%;
      }
    `}

  ${({ hasError, theme }) =>
    hasError &&
    css`
      color: ${theme.themeColors.orange2};
    `};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const LabelText = styled.span`
  margin-right: 15px;
  padding-top: 1px;
`;

const Input = styled.input``;

function CheckboxField({
  className,
  disabled,
  label,
  meta: { touched, submitError, error: syncError } = {},
  input: { value, name, onChange, checked } = {},
  showRequiredAsterisk,
}) {
  const errors = formatFieldErrors({
    submitError,
    syncError,
  });
  const hasError = touched && errors && errors.length > 0;

  return (
    <Container checked={value} className={className} disabled={disabled} check>
      <Label
        label={label}
        check
        tabIndex="0"
        hasError={hasError}
        disabled={disabled}
        showRequiredAsterisk={showRequiredAsterisk}
      >
        <Input
          invalid={hasError}
          type="checkbox"
          disabled={disabled}
          onChange={onChange}
          checked={checked}
          value={value}
          name={name}
        />
        <CheckCon hasError={hasError}>
          <Check color="#fff" />
        </CheckCon>
        <LabelText>{label && ` ${label}`}</LabelText>
        <FieldError show={hasError} errors={errors} />
      </Label>
    </Container>
  );
}

CheckboxField.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  meta: PropTypes.object,
  input: PropTypes.object,
  showRequiredAsterisk: PropTypes.bool,
};

CheckboxField.defaultProps = {
  className: "",
  disabled: false,
  label: "",
  meta: {},
  input: {},
  showRequiredAsterisk: false,
};

export default CheckboxField;
